import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, api, keys, type Uuid } from "@shared/api";
import { LIMITS } from "@shared/config";
import { Dialog, Field } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Textarea } from "@shared/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@shared/ui/toggle-group";

/*
 * Сроки бана. В Discord бан всегда навсегда, а наш сервер умеет и временный
 * (`expires_in_days`) — предлагаем те сроки, которыми модераторы думают:
 * остыть, неделя, месяц.
 */
const TERMS = [
  { value: "forever", label: "Навсегда", days: null },
  { value: "1", label: "День", days: 1 },
  { value: "7", label: "Неделя", days: 7 },
  { value: "30", label: "Месяц", days: 30 },
] as const;

/** «Удалить историю сообщений» — те же ступени, что в Discord, не дальше недели. */
const PURGE = [
  { value: "0", label: "Не удалять" },
  { value: "3600", label: "За последний час" },
  { value: "21600", label: "За 6 часов" },
  { value: "43200", label: "За 12 часов" },
  { value: "86400", label: "За сутки" },
  { value: "259200", label: "За 3 дня" },
  { value: "604800", label: "За неделю" },
] as const;

/**
 * Бан по образцу Discord: кого, почему, на сколько.
 *
 * Причина необязательна, как там, но видна в списке банов — без неё через
 * месяц никто не вспомнит, за что. Сервер сам проверяет старшинство: банить
 * свою роль и выше он не даст, и его отказ показывается здесь же.
 */
export function BanDialog({
  serverId,
  user,
  onClose,
  onDone,
}: {
  serverId: Uuid;
  user: { id: Uuid; name: string };
  onClose: () => void;
  onDone?: () => void;
}) {
  const cache = useQueryClient();
  const [reason, setReason] = useState("");
  const [term, setTerm] = useState<(typeof TERMS)[number]["value"]>("forever");
  const [purge, setPurge] = useState<string>("0");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function ban() {
    setBusy(true);
    setProblem(null);
    try {
      await api.bans.ban(serverId, user.id, {
        reason: reason.trim() || undefined,
        expires_in_days: TERMS.find((item) => item.value === term)?.days ?? null,
        delete_message_seconds: Number(purge),
      });
      await Promise.all([
        cache.invalidateQueries({ queryKey: keys.members(serverId) }),
        cache.invalidateQueries({ queryKey: keys.bans(serverId) }),
      ]);
      onDone?.();
      onClose();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не вышло забанить");
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={`Забанить ${user.name}?`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="destructive" type="button" disabled={busy} onClick={() => void ban()}>
            Забанить
          </Button>
        </>
      }
    >
      <p className="small muted" style={{ margin: 0 }}>
        {user.name} уйдёт с сервера и не сможет вернуться по приглашению, пока бан не снимут или не
        выйдет срок.
      </p>
      <Field label="Срок">
        <ToggleGroup value={term} onValueChange={(next) => next && setTerm(next as typeof term)} aria-label="Срок бана">
          {TERMS.map((item) => (
            <ToggleGroupItem key={item.value} value={item.value}>
              {item.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>
      <Field label="Удалить сообщения" hint="Во всех каналах сервера, вместе с постами форума.">
        <Select value={purge} onValueChange={setPurge}>
          <SelectTrigger aria-label="Удалить сообщения">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PURGE.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Причина" hint={`${reason.length} / ${LIMITS.banReason.max} · видна в списке банов`} error={problem ?? undefined}>
        <Textarea
          value={reason}
          maxLength={LIMITS.banReason.max}
          rows={3}
          placeholder="Необязательно"
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>
    </Dialog>
  );
}
